import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import { ApplicationForm, IPCvData, Publication, PublicationIdentifierType } from '../types';
import Button from 'shared/components/UC/Button';
import FileUpload from './FileUpload';
import styles from './PublicationsSection.module.css';
import { useFormSave } from '../hooks/useFormSave';
import { useIPSync } from '../hooks/useIPSync';
import {
  getSipaPublications,
  SipaPublication,
} from '../mocks/sipaPublications';
import {
  IDENTIFIER_TYPES,
  identifierTypeLabel,
  getPublicationLink,
  publicationKey,
  doiUrl,
} from '../utils/publications';

import { combineClasses } from '../utils/combineClasses';

// CV Narrative Component
interface CVNarrativeProps {
  ipIndex: number;
  cvData: IPCvData;
  errors: any;
  register: any;
  readOnly?: boolean;
}

const CVNarrative: React.FC<CVNarrativeProps> = ({
  ipIndex,
  cvData,
  errors,
  register,
  readOnly = false,
}) => (
  <div className={styles.subsectionGroup}>
    <h3 className={styles.subsectionTitle}>Narrativa del CV *</h3>
    <small className={styles.helpText}>
      Explique cómo su experiencia y las publicaciones adjuntas demuestran su
      capacidad para llevar a cabo este proyecto de investigación.
    </small>
    <textarea
      className={combineClasses(
        styles.textarea,
        (errors.ipCvData as any)?.[ipIndex]?.cvNarrative && styles.error,
        readOnly && styles.readOnlyField
      )}
      id={`cv-narrative-${ipIndex}`}
      {...register(`ipCvData.${ipIndex}.cvNarrative`, {
        required: 'La reseña narrativa del CV es requerida',
        maxLength: {
          value: 3000,
          message: 'La narrativa no puede exceder 3000 caracteres',
        },
      })}
      placeholder="Relación de su trayectoria académica con su proyecto"
      readOnly={readOnly}
    />
    <div
      className={combineClasses(
        styles.characterCount,
        cvData.cvNarrative &&
          cvData.cvNarrative.length > 2400 &&
          styles.nearLimit
      )}
    >
      {(cvData.cvNarrative?.length || 0).toLocaleString('es-CL')} / 3.000 caracteres
    </div>
    {(errors.ipCvData as any)?.[ipIndex]?.cvNarrative && (
      <span className={styles.errorMessage}>
        {(errors.ipCvData as any)[ipIndex].cvNarrative.message}
      </span>
    )}
  </div>
);

// Publications picker modal + table
interface PublicationsSubSectionProps {
  ipIndex: number;
  cvData: IPCvData;
  readOnly: boolean;
  addPublications: (ipIndex: number, pubs: Publication[]) => void;
  removePublication: (ipIndex: number, pubIndex: number) => void;
  setPublicationFile: (
    ipIndex: number,
    pubIndex: number,
    fileId: string | null
  ) => void;
}

const PublicationsSubSection: React.FC<PublicationsSubSectionProps> = ({
  ipIndex,
  cvData,
  readOnly,
  addPublications,
  removePublication,
  setPublicationFile,
}) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [pending, setPending] = useState<Set<string>>(new Set());
  // PA2: alta manual (título + link) dentro del mismo modal.
  const [manualOpen, setManualOpen] = useState(false);
  const [manualTitle, setManualTitle] = useState('');
  const [manualLink, setManualLink] = useState('');
  const [manualIdType, setManualIdType] = useState<PublicationIdentifierType>('DOI');
  const [manualIdValue, setManualIdValue] = useState('');
  const [manualDate, setManualDate] = useState('');
  // A1: adjunto condicional — obligatorio solo cuando no hay enlace de acceso
  // (regla del año pasado: enlace O archivo, al menos uno).
  const [manualFileIds, setManualFileIds] = useState<string[]>([]);
  const manualFormRef = useRef<HTMLDivElement>(null);
  // A2 (sync 2026-07-13, sugerencia de Andrés): con DOI seleccionado, el enlace
  // de acceso se precarga (https://doi.org/…) y se va construyendo con el valor
  // del identificador. Recordamos el último enlace autogenerado para no pisar
  // un enlace que el usuario haya editado a mano.
  const lastAutoLinkRef = useRef('');

  const syncLinkFromIdentifier = (
    type: PublicationIdentifierType,
    value: string
  ) => {
    if (type !== 'DOI') {
      // Al salir de DOI, si el enlace era autogenerado se limpia (no dejar un
      // https://doi.org/ huérfano para ISBN/ROOT/Otro).
      if (manualLink === lastAutoLinkRef.current) {
        setManualLink('');
        lastAutoLinkRef.current = '';
      }
      return;
    }
    const auto = value.trim() ? doiUrl(value) : 'https://doi.org/';
    if (!manualLink.trim() || manualLink === lastAutoLinkRef.current) {
      setManualLink(auto);
      lastAutoLinkRef.current = auto;
    }
  };

  const sipaList = useMemo(
    () => getSipaPublications(cvData.investigatorId),
    [cvData.investigatorId]
  );

  const filtered = useMemo(() => {
    const term = searchTerm.toLowerCase();
    return sipaList.filter((p) => p.title.toLowerCase().includes(term));
  }, [sipaList, searchTerm]);

  const selectedKeys = useMemo(
    () => new Set((cvData.publications || []).map(publicationKey)),
    [cvData.publications]
  );

  const remaining = 5 - (cvData.publications?.length || 0);

  // PA2: al abrir el formulario manual, hacerlo visible (auto-scroll mínimo).
  useEffect(() => {
    if (manualOpen) {
      manualFormRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      });
    }
  }, [manualOpen]);

  const togglePending = (pub: SipaPublication) => {
    if (selectedKeys.has(pub.doi)) return;
    setPending((prev) => {
      const next = new Set(prev);
      if (next.has(pub.id)) {
        next.delete(pub.id);
      } else if (next.size < remaining) {
        next.add(pub.id);
      }
      return next;
    });
  };

  // SIPA entrega DOI; se mapea al modelo genérico (PA1). El enlace de acceso
  // se deriva del DOI (PA3), no se guarda archivo para descarga (PA5).
  const pendingToPublications = (): Publication[] =>
    sipaList
      .filter((p) => pending.has(p.id))
      .slice(0, remaining)
      .map((p) => ({
        source: 'sipa' as const,
        title: p.title,
        publishedAt: p.publishedAt,
        identifierType: 'DOI' as const,
        identifierValue: p.doi,
      }));

  const handleAdd = () => {
    addPublications(ipIndex, pendingToPublications());
    setPending(new Set());
    setSearchTerm('');
    setModalOpen(false);
  };

  const resetManual = () => {
    setManualOpen(false);
    setManualTitle('');
    setManualLink('');
    setManualDate('');
    setManualIdType('DOI');
    setManualIdValue('');
    setManualFileIds([]);
    lastAutoLinkRef.current = '';
  };

  // Nuevo requerimiento (2026-07-21): el archivo adjunto es OBLIGATORIO en toda
  // publicación — es la única evidencia que garantiza acceso al evaluador (un
  // DOI puede ser de pago). El enlace/identificador quedan como metadato de
  // referencia opcional.
  const manualHasFile = manualFileIds.length > 0;

  // A5: la carga manual y la selección del listado son UNA sola confirmación —
  // al agregar la manual también se agregan las seleccionadas (antes la
  // selección se perdía en silencio al cerrar el modal).
  const manualFitsCap = 1 + pending.size <= remaining;

  const handleAddManual = () => {
    // Manual = título + fecha + archivo (obligatorio). Enlace e identificador
    // son opcionales (referencia).
    if (!manualTitle.trim() || !manualHasFile || !manualDate || !manualFitsCap)
      return;
    const pub: Publication = {
      source: 'manual',
      title: manualTitle.trim(),
      publishedAt: manualDate,
      accessUrl: manualLink.trim() || null,
      identifierType: manualIdValue.trim() ? manualIdType : null,
      identifierValue: manualIdValue.trim() || null,
      // Evidencia de acceso requerida.
      fileId: manualFileIds[0],
    };
    addPublications(ipIndex, [...pendingToPublications(), pub]);
    setPending(new Set());
    setSearchTerm('');
    resetManual();
    setModalOpen(false);
  };

  const openModal = () => {
    setPending(new Set());
    setSearchTerm('');
    resetManual();
    setModalOpen(true);
  };

  const formatDate = (iso: string) =>
    new Date(iso).toLocaleDateString('es-CL', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });

  return (
    <div className={styles.subsectionGroup}>
      <h3 className={styles.subsectionTitle}>
        Publicaciones ({cvData.publications?.length || 0}/5 máximo)
      </h3>

      <div className={styles.publicationsSection}>
        {!cvData.publications || cvData.publications.length === 0 ? (
          <div className={styles.emptyState}>
            No hay publicaciones agregadas. Podrá seleccionar de las
            publicaciones registradas en el sistema SIPA asociadas a este
            investigador, o cargarlas manualmente. Puede incluir hasta 5.
            Cada publicación requiere adjuntar su archivo para que el evaluador
            pueda revisarla.
          </div>
        ) : (
          <div className={styles.pubCardList}>
            {cvData.publications.map((pub, pubIndex) => {
              // Identificador como referencia (el archivo es la evidencia).
              const link = getPublicationLink(pub);
              const identifierRef = pub.identifierValue ? (
                <span className={styles.pubIdentifier}>
                  <span className={styles.pubIdentifierType}>
                    {identifierTypeLabel(pub.identifierType)}
                  </span>
                  {link ? (
                    <a href={link} target="_blank" rel="noopener noreferrer">
                      {pub.identifierValue}
                    </a>
                  ) : (
                    <code>{pub.identifierValue}</code>
                  )}
                </span>
              ) : link ? (
                <a href={link} target="_blank" rel="noopener noreferrer">
                  Ver publicación
                </a>
              ) : null;

              return (
                <div key={pubIndex} className={styles.pubCard}>
                  <div className={styles.pubCardHeader}>
                    <div className={styles.pubCardMeta}>
                      <span className={styles.pubCardTitle}>
                        {pub.title || 'Sin título'}
                      </span>
                      <span className={styles.pubCardMetaLine}>
                        <span>
                          {pub.publishedAt
                            ? formatDate(pub.publishedAt)
                            : 'Sin fecha'}
                        </span>
                        {identifierRef && (
                          <>
                            <span className={styles.pubCardMetaSep}>·</span>
                            {identifierRef}
                          </>
                        )}
                      </span>
                    </div>
                    {!readOnly && (
                      <button
                        type="button"
                        className={styles.pubRemoveBtn}
                        onClick={() => removePublication(ipIndex, pubIndex)}
                        aria-label="Eliminar publicación"
                      >
                        <i className="uc-icon icon-size--sm icon-color--red">
                          delete
                        </i>
                      </button>
                    )}
                  </div>

                  {/* Archivo adjunto OBLIGATORIO. Las SIPA llegan sin archivo
                      (ámbar "Falta archivo") hasta que el IR lo adjunta; las
                      manuales nacen con archivo. */}
                  <div className={styles.pubCardFile}>
                    {/* En EDICIÓN la falta de archivo se señala pintando el
                        dropzone en amarillo (prop `missing` del FileUpload): el
                        color basta para destacar contra la UI gris/azul, sin
                        texto de aviso redundante. Con archivo cargado el propio
                        FileUpload (nombre + tamaño + Descargar) ya lo muestra.
                        En readOnly no se renderiza el archivo, así que ahí sí se
                        confirma con texto ("Archivo adjunto"). */}
                    {readOnly ? (
                      pub.fileId ? (
                        <span className={styles.pubFileOk}>Archivo adjunto</span>
                      ) : (
                        <span className={styles.pubCardReadonlyNote}>
                          Sin archivo adjunto.
                        </span>
                      )
                    ) : (
                      <FileUpload
                        sectionType="PROJECT_ATTACHMENTS"
                        label=""
                        compact
                        options={{
                          acceptedTypes: ['.pdf', '.doc', '.docx'],
                          maxFiles: 1,
                        }}
                        value={pub.fileId ? [pub.fileId] : []}
                        onChange={(ids) =>
                          setPublicationFile(ipIndex, pubIndex, ids[0] ?? null)
                        }
                        required={!pub.fileId}
                        missing={!pub.fileId}
                      />
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {!readOnly && remaining > 0 && (
          <Button
            type="button"
            variant="primary"
            onClick={openModal}
            style={{ marginTop: '1em' }}
          >
            + Agregar Publicación
          </Button>
        )}
      </div>

      {/* Modal */}
      {modalOpen && (
        <div
          className={styles.modalOverlay}
          onClick={() => setModalOpen(false)}
        >
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h2 className={styles.modalTitle}>
                Publicaciones de {cvData.investigatorName}
              </h2>
              <button
                type="button"
                className={styles.modalClose}
                onClick={() => setModalOpen(false)}
                aria-label="Cerrar"
              >
                <i className="uc-icon">close</i>
              </button>
            </div>

            <div className={styles.modalSearch}>
              <input
                type="text"
                className={styles.searchInput}
                placeholder="Buscar por título de publicación..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                autoFocus
              />
            </div>

            <div className={styles.modalBody}>
              {sipaList.length === 0 ? (
                <p className={styles.modalEmpty}>
                  No se encontraron publicaciones registradas en SIPA para este
                  investigador.
                </p>
              ) : filtered.length === 0 ? (
                <p className={styles.modalEmpty}>
                  No hay publicaciones que coincidan con la búsqueda.
                </p>
              ) : (
                <ul className={styles.pubPickerList}>
                  {filtered.map((pub) => {
                    const alreadyAdded = selectedKeys.has(pub.doi);
                    const isPending = pending.has(pub.id);
                    const limitReached =
                      pending.size >= remaining && !isPending;
                    const isDisabled =
                      alreadyAdded || (!isPending && limitReached);
                    return (
                      <li
                        key={pub.id}
                        className={combineClasses(
                          styles.pubPickerItem,
                          isDisabled && styles.pubPickerItemDisabled
                        )}
                        onClick={() => !isDisabled && togglePending(pub)}
                      >
                        <input
                          type="checkbox"
                          checked={isPending || alreadyAdded}
                          disabled={isDisabled}
                          onChange={() => !isDisabled && togglePending(pub)}
                          onClick={(e) => e.stopPropagation()}
                          className={styles.pubPickerCheckbox}
                        />
                        <div className={styles.pubPickerInfo}>
                          <span className={styles.pubPickerTitle}>
                            {pub.title}
                          </span>
                          <span className={styles.pubPickerMeta}>
                            {pub.journal} &middot; {formatDate(pub.publishedAt)}{' '}
                            &middot; {pub.doi}
                          </span>
                          {alreadyAdded && (
                            <span className={styles.pubPickerAdded}>
                              Ya agregada
                            </span>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}

              {/* PA2: formulario de carga manual — aparece sobre el CTA sticky */}
              {manualOpen && (
                <div ref={manualFormRef} className={styles.manualForm}>
                  {/* A4: wording amplio — "producción académica" cubre también
                      obras, catálogo de arte, etc. (dirección de María Elena). */}
                  <label className={styles.manualLabel}>
                    Título de la producción académica *
                    <input
                      type="text"
                      className={styles.manualInput}
                      value={manualTitle}
                      onChange={(e) => setManualTitle(e.target.value)}
                      placeholder="Título completo de la producción académica"
                      maxLength={300}
                    />
                  </label>
                  {/* A2: el identificador va ANTES del enlace — al elegir DOI,
                      el enlace se precarga y se construye desde el valor. */}
                  <div className={styles.manualIdRow}>
                    <label className={styles.manualLabel}>
                      Tipo de identificador (opcional)
                      <select
                        className={styles.manualInput}
                        value={manualIdType}
                        onChange={(e) => {
                          const type = e.target
                            .value as PublicationIdentifierType;
                          setManualIdType(type);
                          syncLinkFromIdentifier(type, manualIdValue);
                        }}
                      >
                        {IDENTIFIER_TYPES.map((t) => (
                          <option key={t.value} value={t.value}>
                            {t.label}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label className={styles.manualLabel}>
                      Valor (opcional)
                      <input
                        type="text"
                        className={styles.manualInput}
                        value={manualIdValue}
                        onChange={(e) => {
                          setManualIdValue(e.target.value);
                          syncLinkFromIdentifier(manualIdType, e.target.value);
                        }}
                        placeholder="Ej.: 10.1234/abcd o 978-..."
                        maxLength={200}
                      />
                    </label>
                  </div>
                  <label className={styles.manualLabel}>
                    Enlace de acceso (opcional)
                    <input
                      type="url"
                      className={styles.manualInput}
                      value={manualLink}
                      onChange={(e) => setManualLink(e.target.value)}
                      placeholder="https://..."
                      maxLength={500}
                    />
                  </label>
                  <label className={styles.manualLabel}>
                    Fecha de publicación *
                    <input
                      type="date"
                      className={styles.manualInput}
                      value={manualDate}
                      onChange={(e) => setManualDate(e.target.value)}
                    />
                  </label>
                  {/* Archivo adjunto OBLIGATORIO: única evidencia que garantiza
                      el acceso del evaluador (el DOI puede ser de pago). */}
                  {/* compact: el espaciado inferior lo da el gap de .manualForm
                      (evita el margin-bottom: 2rem exagerado del contenedor) y
                      oculta el header redundante "Archivos cargados". El asterisco
                      lo agrega FileUpload por `required`; no repetirlo en el label. */}
                  <FileUpload
                    sectionType="PROJECT_ATTACHMENTS"
                    label="Adjuntar archivo de la publicación"
                    description="Adjunte el documento (PDF/DOC) para que el evaluador pueda revisarlo. Es obligatorio aunque exista un enlace DOI."
                    compact
                    options={{
                      acceptedTypes: ['.pdf', '.doc', '.docx'],
                      maxFiles: 1,
                    }}
                    value={manualFileIds}
                    onChange={setManualFileIds}
                    required
                    missing={!manualHasFile}
                  />
                  {/* En este kit los estilos están invertidos: `secondary` =
                      azul sólido (acción primaria) y `primary` = blanco/outline.
                      Ver docs/desviaciones-ui.md. NO cambiar a "primary": se vería
                      outline. */}
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={handleAddManual}
                    style={{ alignSelf: 'flex-start' }}
                    disabled={
                      !manualTitle.trim() ||
                      !manualHasFile ||
                      !manualDate ||
                      !manualFitsCap
                    }
                  >
                    {pending.size > 0
                      ? `Agregar manual y ${pending.size} seleccionada(s)`
                      : 'Agregar manualmente'}
                  </Button>
                  {!manualFitsCap && (
                    <span className={styles.pubPickerAdded}>
                      Sin cupo para la carga manual: deseleccione una del
                      listado o elimine una ya agregada (máximo 5).
                    </span>
                  )}
                </div>
              )}

              {/* PA2: CTA sticky al fondo del listado. Si la publicación no está en
                  la lista, se carga manualmente (inmediato, resuelve esta
                  postulación). El registro en SIPA se quitó: sacaba al IR del flujo
                  de formulación (fricción); la carga manual basta. */}
              <div className={styles.manualPrompt}>
                <span className={styles.manualPromptText}>
                  ¿Tu publicación no está en la lista?
                </span>
                <Button
                  type="button"
                  variant="primary"
                  size="sm"
                  onClick={() => setManualOpen((o) => !o)}
                >
                  {manualOpen ? (
                    'Cancelar carga manual'
                  ) : (
                    <>
                      Añadir publicación
                      <svg
                        className={styles.manualToggleIcon}
                        width="16"
                        height="20"
                        viewBox="0 0 16 20"
                        fill="none"
                        aria-hidden="true"
                      >
                        <path
                          d="M10 0H2C0.9 0 0.0100002 0.9 0.0100002 2L0 18C0 19.1 0.89 20 1.99 20H14C15.1 20 16 19.1 16 18V6L10 0ZM12 14H9V17H7V14H4V12H7V9H9V12H12V14ZM9 7V1.5L14.5 7H9Z"
                          fill="currentColor"
                        />
                      </svg>
                    </>
                  )}
                </Button>
              </div>
            </div>

            <div className={styles.modalFooter}>
              {/* A5: feedback del total sobre el máximo (n/5), no solo de la
                  selección del modal. */}
              <span className={styles.modalCount}>
                {(() => {
                  const added = 5 - remaining;
                  const total = added + pending.size;
                  if (pending.size >= remaining)
                    return `Límite alcanzado: quedará en ${total}/5`;
                  if (pending.size > 0)
                    return `${pending.size} seleccionada(s) · quedará en ${total}/5`;
                  return added > 0
                    ? `Tiene ${added}/5 agregada(s) · puede seleccionar hasta ${remaining} más`
                    : `Puede seleccionar hasta ${remaining} publicación(es)`;
                })()}
              </span>
              <div className={styles.modalActions}>
                <Button
                  type="button"
                  variant="primary"
                  onClick={() => setModalOpen(false)}
                >
                  Cancelar
                </Button>
                <Button
                  type="button"
                  variant="cta"
                  onClick={handleAdd}
                  disabled={pending.size === 0}
                >
                  Añadir selección
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// DOI File Component
interface DOIFileProps {
  ipIndex: number;
  cvData: IPCvData;
  control: any;
  readOnly?: boolean;
}

const DOIFile: React.FC<DOIFileProps> = ({
  ipIndex,
  cvData,
  control,
  readOnly,
}) => (
  <div className={styles.subsectionGroup}>
    <h3 className={styles.subsectionTitle}>
      Declaración Obligatoria Interna (DOI)
    </h3>
    <Controller
      name={
        ipIndex === 0 ? 'principalInvestigatorDoiFile' : 'coInvestigatorDoiFile'
      }
      control={control}
      rules={{ required: `La DOI del ${cvData.investigatorName} es requerida` }}
      render={({ field: { value, onChange }, fieldState: { error } }) => {
        const roleLabel =
          cvData.role === 'IR'
            ? 'Investigador Responsable'
            : cvData.role === 'IA'
              ? 'Investigador Asociado'
              : 'Investigador Principal';

        const templateFile =
          cvData.role === 'IR'
            ? {
                url: '/templates/FORMATO DOI IR.docx',
                filename: 'FORMATO DOI IR.docx',
                label: 'Descargar plantilla DOI para Investigador Responsable',
              }
            : cvData.role === 'IA'
              ? {
                  url: '/templates/FORMATO DOI IA.docx',
                  filename: 'FORMATO DOI IA.docx',
                  label: 'Descargar plantilla DOI para Investigador Asociado',
                }
              : {
                  url: '/templates/FORMATO DOI IP.docx',
                  filename: 'FORMATO DOI IP.docx',
                  label: 'Descargar plantilla DOI para Investigador Principal',
                };

        return (
          <FileUpload
            sectionType="PROJECT_ATTACHMENTS"
            label=""
            description={`Adjunte la Declaración Obligatoria Interna (DOI) completada y firmada para ${cvData.role === 'IR' ? 'el' : 'este'} ${roleLabel}.`}
            options={{
              acceptedTypes: ['.pdf', '.doc', '.docx'],
              maxFiles: 1,
            }}
            value={value ? [value] : []}
            onChange={(fileIds) => onChange(fileIds[0] || '')}
            error={error?.message}
            required
            disabled={readOnly}
            templateFile={templateFile}
          />
        );
      }}
    />
  </div>
);

// IP Section Component
interface IPSectionProps {
  cvData: IPCvData;
  ipIndex: number;
  errors: any;
  register: any;
  control: any;
  addPublications: (ipIndex: number, pubs: Publication[]) => void;
  removePublication: (ipIndex: number, pubIndex: number) => void;
  setPublicationFile: (
    ipIndex: number,
    pubIndex: number,
    fileId: string | null
  ) => void;
  readOnly?: boolean;
}

const IPSection: React.FC<IPSectionProps> = ({
  cvData,
  ipIndex,
  errors,
  register,
  control,
  addPublications,
  removePublication,
  setPublicationFile,
  readOnly = false,
}) => {
  return (
    <section className={styles.section}>
      <div className={styles.sectionHeader}>
        <h2 className={styles.sectionTitle}>
          {cvData.investigatorName || 'Investigador UC'}
          {cvData.role === 'IR'
            ? ' (Investigador Responsable)'
            : cvData.role === 'IA'
              ? ' (Investigador Asociado)'
              : ' (Investigador Principal)'}
        </h2>
      </div>

      <div className={styles.sectionContent}>
        <CVNarrative
          ipIndex={ipIndex}
          cvData={cvData}
          errors={errors}
          register={register}
          readOnly={readOnly}
        />

        <PublicationsSubSection
          ipIndex={ipIndex}
          cvData={cvData}
          readOnly={readOnly}
          addPublications={addPublications}
          removePublication={removePublication}
          setPublicationFile={setPublicationFile}
        />

        <DOIFile
          ipIndex={ipIndex}
          cvData={cvData}
          control={control}
          readOnly={readOnly}
        />
      </div>
    </section>
  );
};

// Main Publications Section Component
interface PublicationsSectionProps {
  readOnly?: boolean;
}

const PublicationsSection: React.FC<PublicationsSectionProps> = ({
  readOnly = false,
}) => {
  const {
    register,
    formState: { errors },
    watch,
    setValue,
    control,
  } = useFormContext<ApplicationForm>();
  const { setUpdating } = useFormSave({ stepId: 'publications' });

  // Use the shared IP synchronization hook
  const { getAllIPs } = useIPSync(setUpdating);

  const coInvestigators = watch('coInvestigators');
  const ipCvData = watch('ipCvData');
  const hasConflictOfInterest = watch('hasConflictOfInterest');
  const conflictOfInterestList = watch('conflictOfInterestList');
  const suggestedReviewers = watch('suggestedReviewers');
  const institutionCommitmentLetters = watch('institutionCommitmentLetters');

  const allIPs = getAllIPs();

  const addPublications = (ipIndex: number, pubs: Publication[]) => {
    if (readOnly) return;
    const current = ipCvData?.[ipIndex]?.publications || [];
    const remaining = 5 - current.length;
    setValue(`ipCvData.${ipIndex}.publications`, [
      ...current,
      ...pubs.slice(0, remaining),
    ]);
  };

  const removePublication = (ipIndex: number, pubIndex: number) => {
    if (readOnly) return; // Don't allow changes in read-only mode
    const currentPublications = ipCvData?.[ipIndex]?.publications || [];
    const updatedPublications = currentPublications.filter(
      (_, index) => index !== pubIndex
    );
    setValue(`ipCvData.${ipIndex}.publications`, updatedPublications);
  };

  // Fija/actualiza el archivo adjunto de una publicación ya agregada (para
  // resolver el adjunto obligatorio de las publicaciones SIPA, que llegan sin
  // archivo, o reemplazar el de una manual).
  const setPublicationFile = (
    ipIndex: number,
    pubIndex: number,
    fileId: string | null
  ) => {
    if (readOnly) return;
    const currentPublications = ipCvData?.[ipIndex]?.publications || [];
    const updated = currentPublications.map((pub, index) =>
      index === pubIndex ? { ...pub, fileId } : pub
    );
    setValue(`ipCvData.${ipIndex}.publications`, updated);
  };

  const addSuggestedReviewer = () => {
    if (readOnly) return; // Don't allow changes in read-only mode
    const current = suggestedReviewers || [];
    setValue('suggestedReviewers', [
      ...current,
      { name: '', institution: '', email: '' },
    ]);
  };

  const removeSuggestedReviewer = (index: number) => {
    if (readOnly) return; // Don't allow changes in read-only mode
    const current = suggestedReviewers || [];
    setValue(
      'suggestedReviewers',
      current.filter((_, i) => i !== index)
    );
  };

  const addConflictOfInterest = () => {
    if (readOnly) return; // Don't allow changes in read-only mode
    const current = conflictOfInterestList || [];
    if (current.length < 3) {
      setValue('conflictOfInterestList', [
        ...current,
        { name: '', institution: '' },
      ]);
    }
  };

  const removeConflictOfInterest = (index: number) => {
    if (readOnly) return; // Don't allow changes in read-only mode
    const updated = (conflictOfInterestList || []).filter(
      (_, i) => i !== index
    );
    setValue('conflictOfInterestList', updated);
  };

  const addInstitutionCommitmentLetter = () => {
    if (readOnly) return; // Don't allow changes in read-only mode
    const current = institutionCommitmentLetters || [];
    setValue('institutionCommitmentLetters', [...current, '']);
  };

  const removeInstitutionCommitmentLetter = (index: number) => {
    if (readOnly) return; // Don't allow changes in read-only mode
    const current = institutionCommitmentLetters || [];
    setValue(
      'institutionCommitmentLetters',
      current.filter((_, i) => i !== index)
    );
  };

  return (
    <div>
      <h2 className={styles.sectionTitle}>
        CV y Publicaciones por IR, IP e IA
      </h2>

      <div className={styles.sectionInfoBox}>
        <strong>Información importante:</strong>
        <ul style={{ marginTop: '0.5rem', paddingLeft: '1.5rem' }}>
          <li>
            El IR, cada IP y cada IA deben proporcionar su narrativa del CV y
            hasta 5 publicaciones
          </li>
          <li>
            Las publicaciones deben ser relevantes al proyecto y mostrar que se
            vincula con el proyecto
          </li>
          <li>
            Se detectaron {allIPs.length} investigador(es) UC (IR, IP e IA) en
            el equipo
          </li>
        </ul>
      </div>

      {/* Section for each IP */}
      {(ipCvData || []).map((cvData, ipIndex) => (
        <IPSection
          key={cvData.investigatorId}
          cvData={cvData}
          ipIndex={ipIndex}
          errors={errors}
          register={register}
          control={control}
          addPublications={addPublications}
          removePublication={removePublication}
          setPublicationFile={setPublicationFile}
          readOnly={readOnly}
        />
      ))}

      {allIPs.length === 0 && (
        <div className={styles.emptyState}>
          No se han definido investigadores UC (IR, IP o IA) en el equipo. Por
          favor, complete la sección "Equipo de Trabajo" primero.
        </div>
      )}

      {/* Cartas de compromiso section - only show if there are COI members */}
      {coInvestigators &&
        coInvestigators.filter((ci) => ci.role === 'COI').length > 0 && (
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>
              Cartas de compromiso de co-investigadores
            </h2>

            <div className={styles.sectionInfoBox}>
              <strong>Información importante:</strong>
              <ul style={{ marginTop: '0.5rem', paddingLeft: '1.5rem' }}>
                <li>
                  Cada Co-investigador debe adjuntar su carta de compromiso
                  firmada
                </li>
                <li>Las plantillas están disponibles en español e inglés</li>
                <li>
                  Se detectaron{' '}
                  {coInvestigators.filter((ci) => ci.role === 'COI').length}{' '}
                  Co-investigador(es) en el equipo
                </li>
              </ul>
            </div>

            {coInvestigators
              .filter((ci) => ci.role === 'COI')
              .map((coInvestigator) => {
                const actualIndex = coInvestigators.findIndex(
                  (ci) => ci === coInvestigator
                );
                return (
                  <div key={actualIndex} className={styles.commitmentCard}>
                    <Controller
                      name={`coInvestigators.${actualIndex}.commitmentLetter`}
                      control={control}
                      rules={{
                        required: `La carta de compromiso de ${coInvestigator.name || `Co-investigador ${actualIndex + 1}`} es requerida`,
                      }}
                      render={({
                        field: { value, onChange },
                        fieldState: { error },
                      }) => {
                        const templateFiles = [
                          {
                            url: '/templates/CARTA COI COMPROMISO SPA.docx',
                            filename: 'CARTA COI COMPROMISO SPA.docx',
                            label: 'Descargar plantilla (Español)',
                          },
                          {
                            url: '/templates/CARTA COI COMPROMISO ENG.docx',
                            filename: 'CARTA COI COMPROMISO ENG.docx',
                            label: 'Descargar plantilla (English)',
                          },
                        ];

                        return (
                          <FileUpload
                            sectionType="PROJECT_ATTACHMENTS"
                            label={`Carta de compromiso - ${coInvestigator.name || `Co-investigador ${actualIndex + 1}`}`}
                            description="Adjunte la carta de compromiso completada y firmada. Puede usar cualquiera de las dos plantillas disponibles."
                            options={{
                              acceptedTypes: ['.pdf', '.doc', '.docx'],
                              maxFiles: 1,
                            }}
                            value={value ? [value] : []}
                            onChange={(fileIds) => onChange(fileIds[0] || '')}
                            error={error?.message}
                            required
                            disabled={readOnly}
                            templateFiles={templateFiles}
                          />
                        );
                      }}
                    />
                  </div>
                );
              })}
          </section>
        )}

      {/* Otras cartas de apoyo */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>Otras cartas de apoyo</h2>

        <div className={styles.infoBox}>
          <p>
            Si participan otras instituciones con aportes (pecuniarios o
            valorizados) adicionales a los de los coinvestigadores, adjunte sus
            cartas de apoyo en esta sección.{' '}
            <a
              href="/templates/CARTA%20APOYO%20SPA.docx"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: 'var(--color-primary)',
                textDecoration: 'underline',
              }}
            >
              <strong>Descargar plantilla (Español)</strong>
            </a>
            {' | '}
            <a
              href="/templates/CARTA%20APOYO%20ENG.docx"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: 'var(--color-primary)',
                textDecoration: 'underline',
              }}
            >
              <strong>Descargar plantilla (English)</strong>
            </a>
          </p>
        </div>

        {!institutionCommitmentLetters ||
        institutionCommitmentLetters.length === 0 ? (
          <div className={styles.emptyState}>
            No hay cartas agregadas (opcional).
          </div>
        ) : (
          institutionCommitmentLetters.map((fileId, index) => (
            <div key={index} className={styles.commitmentCard}>
              <div className={styles.cardHeader}>
                <h3 className={styles.cardTitle}>Carta de Apoyo {index + 1}</h3>
                {!readOnly && (
                  <button
                    type="button"
                    className={styles.btnRemove}
                    onClick={() => removeInstitutionCommitmentLetter(index)}
                    aria-label="Eliminar carta de apoyo"
                  >
                    <i className="uc-icon icon-size--md icon-color--red">delete</i>
                  </button>
                )}
              </div>
              <Controller
                name={`institutionCommitmentLetters.${index}`}
                control={control}
                render={({
                  field: { value, onChange },
                  fieldState: { error },
                }) => {
                  const templateFiles = [
                    {
                      url: '/templates/CARTA COI COMPROMISO SPA.docx',
                      filename: 'CARTA COI COMPROMISO SPA.docx',
                      label: 'Descargar plantilla (Español)',
                    },
                    {
                      url: '/templates/CARTA COI COMPROMISO ENG.docx',
                      filename: 'CARTA COI COMPROMISO ENG.docx',
                      label: 'Descargar plantilla (English)',
                    },
                  ];

                  return (
                    <FileUpload
                      sectionType="PROJECT_ATTACHMENTS"
                      label={``}
                      description="Adjunte la carta de apoyo completada y firmada."
                      options={{
                        acceptedTypes: ['.pdf', '.doc', '.docx'],
                        maxFiles: 1,
                      }}
                      value={value ? [value] : []}
                      onChange={(fileIds) => onChange(fileIds[0] || '')}
                      error={error?.message}
                      disabled={readOnly}
                      templateFiles={templateFiles}
                    />
                  );
                }}
              />
            </div>
          ))
        )}

        {!readOnly &&
          (!institutionCommitmentLetters ||
            institutionCommitmentLetters.length >= 0) && (
            <Button
              type="button"
              variant="primary"
              onClick={addInstitutionCommitmentLetter}
              style={{ marginTop: '1em' }}
            >
              + Agregar Carta
            </Button>
          )}
      </section>

      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>Evaluadores - Sugeridos</h2>

        {suggestedReviewers && suggestedReviewers.length > 0 ? (
          suggestedReviewers.map((_, index) => (
            <div key={index} className={styles.reviewerCard}>
              <div className={styles.cardHeader}>
                <h3 className={styles.cardTitle}>
                  Evaluador Sugerido {index + 1}
                </h3>
                {!readOnly && (
                  <button
                    type="button"
                    className={styles.btnRemove}
                    onClick={() => removeSuggestedReviewer(index)}
                    aria-label="Eliminar evaluador sugerido"
                  >
                    <i className="uc-icon icon-size--md icon-color--red">delete</i>
                  </button>
                )}
              </div>

              <div className={styles.formRow}>
                <div className={styles.formColumn}>
                  <label className={styles.label}>Nombre *</label>
                  <input
                    className={styles.input}
                    {...register(`suggestedReviewers.${index}.name`, {
                      required: 'El nombre es requerido',
                    })}
                    placeholder="Nombre completo"
                    disabled={readOnly}
                  />
                </div>

                <div className={styles.formColumn}>
                  <label className={styles.label}>Institución *</label>
                  <input
                    className={styles.input}
                    {...register(`suggestedReviewers.${index}.institution`, {
                      required: 'La institución es requerida',
                    })}
                    placeholder="Institución de afiliación"
                    disabled={readOnly}
                  />
                </div>
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label}>Email</label>
                <input
                  className={combineClasses(styles.input, styles.inputM)}
                  type="email"
                  {...register(`suggestedReviewers.${index}.email`)}
                  placeholder="email@ejemplo.com"
                  disabled={readOnly}
                />
              </div>
            </div>
          ))
        ) : (
          <div className={styles.emptyState}>
            No hay evaluadores sugeridos. Puede agregar hasta tres.
          </div>
        )}

        {!readOnly &&
          (suggestedReviewers?.length < 3 || !suggestedReviewers) && (
            <Button
              type="button"
              variant="primary"
              onClick={addSuggestedReviewer}
              style={{ marginTop: '1em' }}
            >
              + Agregar Evaluador Sugerido
            </Button>
          )}
      </section>

      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>
          Evaluadores - Posible conflicto de interés (
          {conflictOfInterestList?.length || 0}/3 máximo)
        </h2>

        <div className={styles.formGroup}>
          <label className={styles.label}>
            ¿Existe algún conflicto de interés? *
          </label>
          <div className={styles.radioGroup}>
            <label className={styles.radioLabel}>
              <input
                type="radio"
                {...register('hasConflictOfInterest', {
                  required: 'Debe indicar si existe conflicto de interés',
                })}
                value="SI"
                disabled={readOnly}
              />
              Sí
            </label>
            <label className={styles.radioLabel}>
              <input
                type="radio"
                {...register('hasConflictOfInterest')}
                value="NO"
                disabled={readOnly}
              />
              No
            </label>
          </div>
          {errors.hasConflictOfInterest && (
            <span className={styles.errorMessage}>
              {errors.hasConflictOfInterest.message}
            </span>
          )}
        </div>

        {hasConflictOfInterest === 'SI' && (
          <div>
            {conflictOfInterestList && conflictOfInterestList.length > 0 ? (
              conflictOfInterestList.map((_, index) => (
                <div key={index} className={styles.conflictCard}>
                  <div className={styles.cardHeader}>
                    <h4 className={styles.cardTitle}>
                      Conflicto de Interés {index + 1}
                    </h4>
                    {!readOnly && (
                      <button
                        type="button"
                        className={styles.btnRemove}
                        onClick={() => removeConflictOfInterest(index)}
                        aria-label="Eliminar conflicto de interés"
                      >
                        <i className="uc-icon icon-size--md icon-color--red">delete</i>
                      </button>
                    )}
                  </div>

                  <div className={styles.formRow}>
                    <div className={styles.formColumn}>
                      <label className={styles.label}>Nombre *</label>
                      <input
                        className={styles.input}
                        {...register(`conflictOfInterestList.${index}.name`, {
                          required: 'El nombre es requerido',
                        })}
                        placeholder="Nombre de la persona"
                        disabled={readOnly}
                      />
                    </div>

                    <div className={styles.formColumn}>
                      <label className={styles.label}>Institución *</label>
                      <input
                        className={styles.input}
                        {...register(
                          `conflictOfInterestList.${index}.institution`,
                          {
                            required: 'La institución es requerida',
                          }
                        )}
                        placeholder="Institución"
                        disabled={readOnly}
                      />
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className={styles.emptyState}>
                No hay conflictos de interés declarados. Agregue los detalles.
              </div>
            )}

            {!readOnly &&
              (conflictOfInterestList?.length < 3 ||
                !conflictOfInterestList) && (
                <Button
                  type="button"
                  variant="primary"
                  onClick={addConflictOfInterest}
                  disabled={(conflictOfInterestList?.length || 0) >= 3}
                  style={{ marginTop: '1em' }}
                >
                  + Agregar Conflicto de Interés
                </Button>
              )}

            {conflictOfInterestList &&
              conflictOfInterestList.length < 3 &&
              conflictOfInterestList.length > 0 && (
                <small
                  className={combineClasses(styles.helpText)}
                  style={{
                    marginTop: '0.5rem',
                    color: 'var(--color-text-secondary)',
                  }}
                >
                  Puede agregar hasta {3 - conflictOfInterestList.length}{' '}
                  conflicto(s) de interés más.
                </small>
              )}
          </div>
        )}
      </section>
    </div>
  );
};

export default PublicationsSection;
